import { Instagram, Mail, MapPin, Phone, Facebook, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { NewsletterField } from "./NewsletterField";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c129d2ac-2209-4214-9ac4-af6d02f0e7d1/artifacts/e10f2m7m_hf_20260429_103436_b903d0ed-1980-49ff-95c4-cf93c75881ec.png";

const SHOP_LINKS = [
  { to: "/shop/earrings", label: "Earrings" },
  { to: "/shop/necklaces", label: "Necklaces" },
  { to: "/shop/bangles", label: "Bangles" },
  { to: "/shop/bridal-sets", label: "Bridal Sets" },
  { to: "/shop", label: "All categories" },
];

const COMPANY_LINKS = [
  { to: "/about", label: "Our story" },
  { to: "/find-us", label: "Find a rikshaw" },
  { to: "/contact", label: "Contact" },
  { to: "/admin", label: "For partners" },
];

const HELP_LINKS = [
  { to: "/shipping", label: "Shipping" },
  { to: "/returns", label: "Returns" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-jow-navy text-jow-cream mt-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-4 space-y-6">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="JewelleryOnWheels"
              className="h-12 w-12 object-contain bg-jow-cream/5 p-1"
            />
            <div>
              <h3 className="font-display text-2xl font-semibold">
                JewelleryOnWheels
              </h3>
              <p className="label-overline text-jow-gold">
                Beauty That Comes To You
              </p>
            </div>
          </div>
          <p className="text-jow-cream/70 max-w-md leading-relaxed">
            Premium Indian artificial jewellery, hand-curated and delivered to
            your doorstep by our fleet of beautifully decorated e-rikshaws.
          </p>
          <ul className="space-y-3 text-sm text-jow-cream/80">
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 text-jow-gold" />
              Delhi · Mumbai · Bangalore · Jaipur · Lucknow · Hyderabad
            </li>
            <li className="flex items-center gap-3">
              <Phone size={16} className="text-jow-gold" /> +91 90000 00000
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-jow-gold" /> care@jewelleryonwheels.in
            </li>
          </ul>
        </div>

        <FooterColumn title="Shop" links={SHOP_LINKS} testid="footer-col-shop" />
        <FooterColumn title="Company" links={COMPANY_LINKS} testid="footer-col-company" />
        <FooterColumn title="Help" links={HELP_LINKS} testid="footer-col-help" />

        <div className="md:col-span-2">
          <p className="label-overline text-jow-gold mb-5">Connect</p>
          <div className="flex items-center gap-3">
            <SocialIcon Icon={Instagram} testid="footer-ig" />
            <SocialIcon Icon={Facebook} testid="footer-fb" />
            <SocialIcon Icon={Youtube} testid="footer-yt" />
          </div>
          <p className="label-overline text-jow-gold mt-7 mb-2">Newsletter</p>
          <NewsletterField />
        </div>
      </div>
      <div className="border-t border-jow-cream/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <p className="text-xs text-jow-cream/55 tracking-wider">
            © {new Date().getFullYear()} JewelleryOnWheels. Crafted in India.
          </p>
          <p className="text-xs text-jow-cream/55 tracking-wider">
            Made with love, navy &amp; gold.
          </p>
        </div>
      </div>
    </footer>
  );
};

function FooterColumn({ title, links, testid }) {
  return (
    <div className="md:col-span-2" data-testid={testid}>
      <p className="label-overline text-jow-gold mb-5">{title}</p>
      <ul className="space-y-3 text-sm text-jow-cream/80">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="hover:text-jow-gold transition-colors"
              data-testid={`${testid}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialIcon({ Icon, testid }) {
  return (
    <a
      href="#social"
      data-testid={testid}
      className="inline-flex items-center justify-center w-10 h-10 border border-jow-gold/40 hover:bg-jow-gold/10 transition"
    >
      <Icon size={16} />
    </a>
  );
}
